import React from "react";
import { Bell, Trash2, CheckCheck, ExternalLink } from "lucide-react";
import { useAlerts, useClearAlerts, useMarkAlertsSeen } from "@/lib/queries";
import { formatPrice, formatRelative, RETAILER_COLORS } from "@/lib/cr";

export default function AlertFeed() {
  const { data: alerts = [], isLoading } = useAlerts(40);
  const clear = useClearAlerts();
  const markSeen = useMarkAlertsSeen();

  return (
    <aside
      className="border-l border-[#262626] h-full overflow-hidden bg-[#050505] flex flex-col"
      data-testid="alert-feed"
    >
      <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Bell className="w-3.5 h-3.5 text-[#EAB308]" />
          <h2 className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-white">
            Live alerts
          </h2>
          <span className="text-[10px] font-mono text-[#737373]">({alerts.length})</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="mark-seen-btn"
            onClick={() => markSeen.mutate()}
            className="p-1.5 text-[#737373] hover:text-[#22C55E] hover:bg-[#1A1A1A] rounded-sm transition-colors"
            title="Mark all as seen"
          >
            <CheckCheck className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="clear-alerts-btn"
            onClick={() => clear.mutate()}
            className="p-1.5 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors"
            title="Clear alerts"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {isLoading ? (
          <div className="text-center text-[#737373] text-xs py-8">Loading...</div>
        ) : alerts.length === 0 ? (
          <div className="text-center text-[#737373] text-xs py-8 px-4 leading-relaxed">
            No alerts yet.<br/>
            <a href="/app/watchlist" className="text-[#EAB308] hover:underline">Add a watchlist rule</a> — matches appear here within ~15 sec.
          </div>
        ) : (
          alerts.map((a) => {
            const color = RETAILER_COLORS[a.retailer_id] || "#A3A3A3";
            return (
              <div
                key={a.id}
                data-testid={`alert-${a.id}`}
                className="slide-in border border-[#262626] bg-[#0d0d0d] p-2.5 rounded-sm hover:border-[#EAB308]/40 transition-colors"
              >
                <div className="flex items-start gap-2 mb-1.5">
                  {!a.seen && <span className="mt-1 w-1.5 h-1.5 bg-[#EAB308] rounded-full shrink-0 animate-pulse" />}
                  <div className="text-xs font-semibold text-white leading-tight line-clamp-2 flex-1">
                    {a.title}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm border"
                    style={{ color: color, borderColor: `${color}55`, backgroundColor: "#0a0a0a" }}
                  >
                    {a.retailer_name}
                  </span>
                  <span className="font-mono text-xs font-bold text-white">
                    {formatPrice(a.price)}
                  </span>
                  <span className="text-[10px] font-mono text-[#737373] ml-auto">
                    {formatRelative(a.triggered_at)}
                  </span>
                </div>
                <div className="text-[10px] text-[#A3A3A3] font-mono mb-2">
                  {a.reason}
                  {a.watch_name && <span className="text-[#737373]"> · {a.watch_name}</span>}
                </div>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`alert-open-${a.id}`}
                  className="text-[10px] uppercase tracking-wider font-bold text-[#EAB308] hover:text-[#FDE047] flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open cart
                </a>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
